import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FaArrowLeft, FaPaperPlane, FaRobot, FaBars, FaCommentDots } from "react-icons/fa";
import { API_BASE_URL } from "../../config/apiConfig";
import { formatAiText } from "../../utils/formatAiText";
import ChatSidebar from "./components/ChatSidebar";
import TypingIndicator from "./components/TypingIndicator";
import PerformancePanel from "./components/PerformancePanel";

const authHeaders = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` });

const Avatar = ({ role }) =>
    role === "user" ? (
        <div className="w-8 h-8 rounded-full bg-blue600 text-white text-xs font-semibold flex items-center justify-center shrink-0">
            You
        </div>
    ) : (
        <div className="w-8 h-8 rounded-full bg-[#2148C0] text-white flex items-center justify-center shrink-0">
            <FaRobot size={13} />
        </div>
    );

const AssistantPage = () => {
    const navigate = useNavigate();
    const [chats, setChats] = useState([]);
    const [activeChatId, setActiveChatId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [profile, setProfile] = useState(null);
    const [readiness, setReadiness] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [panelOpen, setPanelOpen] = useState(true);
    const bottomRef = useRef(null);
    const inputRef = useRef(null);

    const fetchChatMessages = async (chatId) => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/assistant/chats/${chatId}/messages`, { headers: authHeaders() });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to load chat");
            setMessages(data.data.messages || []);
            setActiveChatId(chatId);
        } catch (err) {
            toast.error(err.message);
        }
    };

    const fetchChats = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/assistant/chats`, { headers: authHeaders() });
            const data = await res.json();
            const list = data.data || [];
            setChats(list);
            return list;
        } catch (err) {
            return [];
        }
    };

    useEffect(() => {
        (async () => {
            const list = await fetchChats();
            if (list.length) fetchChatMessages(list[0].id);
        })();

        fetch(`${API_BASE_URL}/api/users/me`, { headers: authHeaders() })
            .then((res) => (res.ok ? res.json() : Promise.reject(new Error("Failed to load profile"))))
            .then((data) => setProfile({ yearGroup: data.yearGroup, subjects: data.subjects }))
            .catch(() => {});

        fetch(`${API_BASE_URL}/api/readiness`, { headers: authHeaders() })
            .then((res) => res.json())
            .then((data) => setReadiness(data.data))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, sending]);

    const handleSelectChat = (chatId) => {
        if (chatId === activeChatId) {
            setSidebarOpen(false);
            return;
        }
        fetchChatMessages(chatId);
        setSidebarOpen(false);
    };

    const handleNewChat = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/assistant/chats`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({}),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to create chat");
            setChats((prev) => [data.data, ...prev]);
            setMessages([]);
            setActiveChatId(data.data.id);
            setSidebarOpen(false);
            inputRef.current?.focus();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleUseWeakSubjects = (question) => {
        setInput(question);
        inputRef.current?.focus();
    };

    const handleSend = async (e) => {
        e.preventDefault();
        const question = input.trim();
        if (!question) return;
        setInput("");
        setMessages((prev) => [...prev, { role: "user", content: question, id: `local-${Date.now()}` }]);
        setSending(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/assistant/ask`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({ question, chatId: activeChatId }),
            });
            const data = await res.json();
            if (!res.ok) {
                console.error("Assistant request failed:", {
                    httpStatus: res.status,
                    message: data.message,
                    error: data.error,
                    provider: data.provider,
                    statusCode: data.statusCode,
                    responseBody: data.responseBody,
                });
                throw new Error(data.error || data.message || "Failed to get a response");
            }
            if (!activeChatId) setActiveChatId(data.chatId);
            setMessages((prev) => [...prev, data.data]);
            fetchChats();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="h-screen bg-gradient-to-b from-blue-50/60 to-gray-50 flex flex-col">
            <nav className="bg-[#2148C0] shadow-md px-4 sm:px-6 py-3.5 flex items-center gap-4 shrink-0">
                <button onClick={() => navigate("/dashboard")} className="text-white/90 hover:text-white">
                    <FaArrowLeft size={18} />
                </button>
                <button onClick={() => setSidebarOpen((v) => !v)} className="text-white/90 hover:text-white lg:hidden">
                    <FaBars size={18} />
                </button>
                <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                    <FaRobot className="text-white" size={16} />
                </div>
                <div>
                    <h1 className="text-base font-bold text-white leading-tight">AI Learning Assistant</h1>
                    <p className="text-[11px] text-white/70 leading-tight hidden sm:block">Ask anything, get worked examples</p>
                </div>
            </nav>

            <div className="relative flex-1 flex overflow-hidden">
                <ChatSidebar
                    chats={chats}
                    activeChatId={activeChatId}
                    onSelectChat={handleSelectChat}
                    onNewChat={handleNewChat}
                    isOpen={sidebarOpen}
                    onClose={() => setSidebarOpen(false)}
                />

                <div className="flex-1 flex flex-col lg:flex-row gap-4 p-3 sm:p-5 overflow-hidden">
                    <div className="flex-1 min-w-0 flex flex-col bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-5">
                            {messages.length === 0 && !sending && (
                                <div className="h-full flex flex-col items-center justify-center text-center gap-3 px-6">
                                    <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center">
                                        <FaCommentDots className="text-[#2148C0]" size={22} />
                                    </div>
                                    <p className="text-gray-700 text-sm font-medium">Ask me anything you're stuck on</p>
                                    <p className="text-gray-400 text-xs max-w-xs">
                                        I'll explain it simply, with worked examples tailored to your class and subjects.
                                    </p>
                                </div>
                            )}
                            {messages.map((m) => (
                                <div key={m.id} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                    {m.role !== "user" && <Avatar role={m.role} />}
                                    <div
                                        className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap break-words ${
                                            m.role === "user"
                                                ? "bg-blue600 text-white rounded-br-md"
                                                : "bg-gray-50 text-gray-800 border border-gray-100 rounded-bl-md"
                                        }`}
                                    >
                                        {m.role === "user" ? m.content : formatAiText(m.content)}
                                    </div>
                                    {m.role === "user" && <Avatar role={m.role} />}
                                </div>
                            ))}
                            {sending && <TypingIndicator />}
                            <div ref={bottomRef} />
                        </div>

                        <form onSubmit={handleSend} className="flex gap-2 p-3 sm:p-4 border-t border-gray-100 bg-white">
                            <input
                                ref={inputRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Type your question..."
                                className="flex-1 border border-gray-200 rounded-full px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue600/30 focus:border-blue600"
                            />
                            <button
                                disabled={sending}
                                className="bg-blue600 text-white rounded-full w-12 h-12 flex items-center justify-center disabled:opacity-50 hover:opacity-90 shrink-0"
                            >
                                <FaPaperPlane size={14} />
                            </button>
                        </form>
                    </div>

                    <PerformancePanel
                        profile={profile}
                        readiness={readiness}
                        onUseWeakSubjects={handleUseWeakSubjects}
                        isOpen={panelOpen}
                        onToggle={() => setPanelOpen((v) => !v)}
                    />
                </div>
            </div>
        </div>
    );
};

export default AssistantPage;
